import { Box, Button, Drawer, Group, Modal } from '@mantine/core'
import { FileRejection } from '@mantine/dropzone'
import { useDisclosure } from '@mantine/hooks'
import { IconBolt } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import { getApiError } from '@/api/helpers/apiError'
import { AnnotationProgressBar } from '@/components/annotations/AnnotationProgressBar/AnnotationProgressBar'
import { getAnnotationPoints } from '@/components/annotations/helpers/getAnnotationPoints'
import { useFeatureUsage } from '@/components/subscriptions/useFeatureUsage'
import { ZohoEmbeddedForm } from '@/components/support/ZohoEmbeddedForm'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { MIN_ANNOTATION_POINTS } from '@/config/annotation'
import { ZOHO_REQUEST_LABELING_FORM_URL } from '@/config/main'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useModelContext } from '@/providers/ModelContext'
import {
  useGetAnnotationCount,
  useGetUploadStatus,
  useInvalidateFileList,
  useUploadFilesToDataset
} from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'
import {
  Dataset,
  DatasetType,
  DatasetVersion,
  DatasetVersionStatus
} from '@/types/dataset'
import { CrudAction, Resource } from '@/types/permissions'
import { CloneDatasetHandler } from '../CloneDatasetHandler/CloneDatasetHandler'
import { ImageCropHandler } from '../ImageCrop/ImageCropHandler'
import { ConfirmUploadModal } from './ConfirmUploadModal/ConfirmUploadModal'
import { UploadProgressModal } from './UploadProgressModal/UploadProgressModal'
import { UploadableMediaList } from './UploadableMediaList'

type RouteState = {
  openCrop?: boolean
}

type UploadableMediaListHandlerProps = {
  dataset: Dataset
  version: DatasetVersion
  showTrainModelButton?: boolean
  onTrainModel?: () => void
  onDatasetCloned: (dataset: Dataset) => void
}

export const UploadableMediaListHandler = ({
  dataset,
  version,
  showTrainModelButton,
  onTrainModel,
  onDatasetCloned
}: UploadableMediaListHandlerProps) => {
  const intl = useIntl()
  const location = useLocation()

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([])
  const [uploadBatchId, setUploadBatchId] = useState<string>('')

  const [
    isConfirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal }
  ] = useDisclosure(false)

  const [
    isProgressModalOpened,
    { open: openProgressModal, close: closeProgressModal }
  ] = useDisclosure(false)

  const routeState = location?.state as RouteState | null

  const [isCropModalOpened, { open: openCropModal, close: closeCropModal }] =
    useDisclosure(routeState?.openCrop || false)

  const [isCloneModalOpened, { open: openCloneModal, close: closeCloneModal }] =
    useDisclosure(false)

  const [
    isRequestLabelingDrawerOpened,
    { open: openRequestLabelingDrawer, close: closeRequestLabelingDrawer }
  ] = useDisclosure(false)

  const [canCreateDataset, canUploadFiles] = useHasPermissions(
    Resource.Datasets,
    [CrudAction.Create, CrudAction.Update]
  )

  const [canCreateTraining] = useHasPermissions(Resource.Trainings, [
    CrudAction.Create
  ])

  const { refreshFeatureUsage } = useFeatureUsage('dataset_storage')

  const { mutateAsync: uploadFiles, isPending: isUploadPending } =
    useUploadFilesToDataset()

  const { data: uploadStatusData } = useGetUploadStatus(
    uploadBatchId,
    uploadBatchId !== ''
  )

  const isUploadFinished =
    uploadStatusData?.status === 'Complete' ||
    uploadStatusData?.status === 'Failed'

  const { invalidateFileList } = useInvalidateFileList(version.id)

  const { application } = useApplicationContext()
  const { model } = useModelContext()

  const { data: annotationCount } = useGetAnnotationCount(version.id)

  const annotatedImagesCount = annotationCount?.images || 0
  const annotatedVideosCount = annotationCount?.videos || 0

  const isTrainingButtonDisabled =
    getAnnotationPoints(annotatedImagesCount, annotatedVideosCount) <
      MIN_ANNOTATION_POINTS || !canCreateTraining

  const isUploadLocked =
    version.status === DatasetVersionStatus.Completed || !canUploadFiles

  useEffect(() => {
    if (
      uploadStatusData?.uploaded_files !== 0 &&
      uploadStatusData?.status === 'Pending'
    ) {
      // Refetch file list as more files were uploaded
      invalidateFileList()
    }

    if (isUploadFinished) {
      invalidateFileList()
      void refreshFeatureUsage()

      if (uploadStatusData?.failed_files > 0) {
        openProgressModal()

        showToast(
          intl.formatMessage(
            { id: 'datasets.upload.filesPartiallyUploaded' },
            { count: uploadStatusData?.failed_files }
          ),
          'error'
        )
      } else {
        setUploadBatchId('')
        closeProgressModal()

        showToast(
          intl.formatMessage({ id: 'datasets.upload.completed' }),
          'success'
        )
      }
    }
  }, [
    intl,
    uploadStatusData,
    isUploadFinished,
    openProgressModal,
    closeProgressModal,
    invalidateFileList,
    refreshFeatureUsage
  ])

  const handleDrop = (files: File[]) => {
    setAcceptedFiles(files)
    openConfirmModal()
  }

  const handleReject = (fileRejections: FileRejection[]) => {
    setFileRejections(fileRejections)
    openConfirmModal()
  }

  const handleCloseConfirmModal = () => {
    closeConfirmModal()
    setAcceptedFiles([])
    setFileRejections([])
  }

  const handleConfirmUpload = async () => {
    try {
      const notification_context = {
        appId: application?.id || '',
        modelId: model?.id || ''
      }
      const { data } = await uploadFiles({
        versionId: version.id,
        media_files: acceptedFiles,
        notification_context: notification_context
      })

      setUploadBatchId(data.batch_id)
      closeConfirmModal()
      openProgressModal()
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'datasets.upload.failed' })

      showToast(message, 'error')
    }
  }

  const handleCloseProgressModal = () => {
    if (isUploadFinished) {
      setUploadBatchId('')
    }

    closeProgressModal()
  }

  const handleCropSuccess = () => {
    closeCropModal()
    invalidateFileList()
    void refreshFeatureUsage()
  }

  const handleRequestLabelingSubmit = () => {
    showToast(
      intl.formatMessage({ id: 'annotation.requestLabeling.success' }),
      'success'
    )

    closeRequestLabelingDrawer()
  }

  return (
    <>
      {version.status === DatasetVersionStatus.Completed && (
        <PlatformAlert
          variant="success"
          title={<FormattedMessage id="datasets.completed.banner.title" />}
          mb="lg"
        >
          <FormattedMessage id="datasets.completed.banner.description" />
        </PlatformAlert>
      )}

      <UploadableMediaList
        versionId={version.id}
        isUploading={uploadStatusData?.status === 'Pending'}
        isLocked={isUploadLocked}
        rightSection={
          <Group>
            {annotationCount !== undefined && (
              <Box w={300}>
                <AnnotationProgressBar
                  annotatedImagesCount={annotatedImagesCount}
                  annotatedVideosCount={annotatedVideosCount}
                />
              </Box>
            )}

            {showTrainModelButton && (
              <Button
                leftSection={<IconBolt size={16} />}
                miw={180}
                color="green"
                disabled={isTrainingButtonDisabled}
                onClick={onTrainModel}
              >
                <FormattedMessage id="models.trainModel" />
              </Button>
            )}
          </Group>
        }
        showAddCropsButton={
          dataset.dataset_type === DatasetType.Secondary && !isUploadLocked
        }
        showCloneButton={canCreateDataset}
        onDrop={handleDrop}
        onReject={handleReject}
        onShowUploadProgress={openProgressModal}
        onAddCrops={openCropModal}
        onClone={openCloneModal}
        onRequestLabeling={openRequestLabelingDrawer}
      />

      <ConfirmUploadModal
        opened={isConfirmModalOpened}
        acceptedFiles={acceptedFiles}
        fileRejections={fileRejections}
        isLoading={isUploadPending}
        isConfirmDisabled={isUploadPending || acceptedFiles.length === 0}
        onClose={handleCloseConfirmModal}
        onConfirmUpload={() => void handleConfirmUpload()}
      />

      <UploadProgressModal
        totalFiles={uploadStatusData?.total_files || 0}
        uploadedFies={uploadStatusData?.uploaded_files || 0}
        isCompleted={isUploadFinished}
        failedFileNames={uploadStatusData?.failed_file_names || []}
        opened={isProgressModalOpened}
        onClose={handleCloseProgressModal}
      />

      <Modal
        size="xl"
        opened={isCropModalOpened}
        title={<FormattedMessage id="datasets.crops.add" />}
        onClose={closeCropModal}
      >
        <ImageCropHandler
          targetDatasetVersionId={version.id}
          onCropSuccess={handleCropSuccess}
          onCancel={closeCropModal}
        />
      </Modal>

      <Modal
        size="xl"
        opened={isCloneModalOpened}
        title={<FormattedMessage id="datasets.clone.title" />}
        onClose={closeCloneModal}
      >
        <CloneDatasetHandler
          originalDataset={dataset}
          originalDatasetVersion={version}
          onCancel={closeCloneModal}
          onDatasetCloned={onDatasetCloned}
        />
      </Modal>

      <Drawer
        size="60rem"
        position="right"
        title={<FormattedMessage id="annotation.requestLabeling.title" />}
        opened={isRequestLabelingDrawerOpened}
        onClose={closeRequestLabelingDrawer}
      >
        {isRequestLabelingDrawerOpened && (
          <ZohoEmbeddedForm
            formUrl={ZOHO_REQUEST_LABELING_FORM_URL}
            onSubmit={handleRequestLabelingSubmit}
          />
        )}
      </Drawer>
    </>
  )
}
